import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, Button, IconButton, Grid, Card, Divider, Chip, Avatar, CircularProgress } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import ArticleIcon from "@mui/icons-material/Article";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DownloadIcon from "@mui/icons-material/Download";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";

import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import RichHtml from "../../components/Common/RichHtml";
import CustomSnackbar from "../Common/snackBar";
import { getArticleByIdAction, getModulesAction } from "../../Actions/KnowledgeBaseAction";
import KnowledgeBaseFormDialog from "./KnowledgeBaseFormDialog";
import TutorialPlayerModal from "./TutorialPlayerModal";
import { getPermissionData, getUserData } from "../../Actions/common";

const KnowledgeBaseViewPage = () => {
  const { moduleSlug, knowledgeBaseId } = useParams();
  const navigate = useNavigate();

  const userData = localStorage.getItem("userData") ? getUserData() : null;
  const permissionData = localStorage.getItem(`${userData?.id}_permissionData`)
    ? getPermissionData(`${userData?.id}_permissionData`)
    : [];
  const hasPermission = permissionData[0]?.role_permissions?.find(
    (m) => m?.module && m?.module?.slug === "knowledge-base"
  );

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allModules, setAllModules] = useState([]);

  const [formDialog, setFormDialog] = useState({ open: false, tutorial: null });
  const [playerModal, setPlayerModal] = useState({ open: false, tutorial: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const showSnack = (message, severity = "success") => setSnackbar({ open: true, message, severity });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getArticleByIdAction(knowledgeBaseId);
      if (res) setArticle(res);
      else showSnack("Article not found", "error");
    } catch (err) {
      showSnack("Failed to load article", "error");
    } finally {
      setLoading(false);
    }
  }, [knowledgeBaseId]);

  useEffect(() => {
    loadData();
    getModulesAction().then(res => setAllModules(Array.isArray(res) ? res : []));
  }, [loadData]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!article) {
    return (
      <Box p={4} textAlign="center">
        <Typography variant="h5">Article not found</Typography>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>Go Back</Button>
      </Box>
    );
  }

  const attachments = Array.isArray(article.attachments) ? article.attachments : [];
  const videos = attachments.filter(a => a.mediaType === "video");
  const images = attachments.filter(a => a.mediaType === "image");
  const docs = attachments.filter(a => a.mediaType === "document" || a.mediaType === "raw");

  // Fallback for legacy video
  if (article.videoUrl && !videos.length) {
    videos.push({
      mediaType: "video",
      url: article.videoUrl,
      type: article.videoSourceType,
      label: "Main Video"
    });
  }

  return (
    <Box id="kt_content_container" sx={{ pb: 10 }}>
      <BreadcrumbsComponent />

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, mt: 2, gap: 2 }}>
        <IconButton onClick={() => navigate(`/knowledge-base/${moduleSlug}`)} sx={{ bgcolor: 'white', boxShadow: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="body1" color="text.secondary">
          Back to {article.moduleName || moduleSlug}
        </Typography>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={8} lg={9}>
          <Card sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, boxShadow: 2 }}>
            {/* Title row with Edit button top-right */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, gap: 2 }}>
              <Typography variant="h4" fontWeight="bold">
                {article.title}
              </Typography>
              {hasPermission?.canEdit && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline btn-outline-primary d-flex align-items-center gap-1"
                  style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                  onClick={() => setFormDialog({ open: true, tutorial: article })}
                >
                  <EditIcon style={{ fontSize: 16 }} />
                  Edit
                </button>
              )}
            </Box>

            {article.shortDescription && (
              <Typography variant="subtitle1" color="text.secondary" mb={3}>
                {article.shortDescription}
              </Typography>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4, pb: 3, borderBottom: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: "text.secondary" }}>
                <CalendarTodayIcon fontSize="small" />
                <Typography variant="body2">{new Date(article.updatedAt || article.createdAt).toLocaleDateString()}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: "text.secondary" }}>
                <VisibilityIcon fontSize="small" />
                <Typography variant="body2">{article.totalViews || 0} views</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: "success.main" }}>
                <ThumbUpAltIcon fontSize="small" />
                <Typography variant="body2">{article.totalHelpful || 0}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: "error.main" }}>
                <ThumbDownAltIcon fontSize="small" />
                <Typography variant="body2">{article.totalUnhelpful || 0}</Typography>
              </Box>
            </Box>

            {/* Videos */}
            {videos.length > 0 && (
              <Box mb={5}>
                <Typography variant="h6" fontWeight={600} mb={2}>Video Guides</Typography>
                <Grid container spacing={2}>
                  {videos.map((vid, idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <Box
                        sx={{
                          bgcolor: 'black',
                          borderRadius: 2,
                          aspectRatio: '16/9',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                        onClick={() => setPlayerModal({ open: true, tutorial: { ...article, videoUrl: vid.url, videoSourceType: vid.type } })}
                      >
                        {article.thumbnail ? (
                          <img src={article.thumbnail} alt="thumbnail" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.7 }} />
                        ) : null}
                        <PlayCircleOutlineIcon sx={{ color: 'white', fontSize: 60, position: 'absolute' }} />
                      </Box>
                      <Typography mt={1} variant="body2" fontWeight={500} align="center">
                        {vid.label || "Video Tutorial"}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* HTML Content */}
            <Box
              sx={{
                lineHeight: 1.8,
                '& img': { maxWidth: '100%', borderRadius: 2, my: 2 },
                '& h2': { mt: 4, mb: 2, fontWeight: 600 },
                '& h3': { mt: 3, mb: 2, fontWeight: 600 },
              }}
            >
              <RichHtml html={article.content} />
            </Box>

            {/* Images Grid */}
            {images.length > 0 && (
              <Box mt={6}>
                <Typography variant="h6" fontWeight={600} mb={2}>Image Gallery</Typography>
                <Grid container spacing={2}>
                  {images.map((img, idx) => (
                    <Grid item xs={12} sm={6} md={4} key={idx}>
                      <a href={img.url} target="_blank" rel="noopener noreferrer">
                        <img src={img.url} alt={img.label} style={{ width: '100%', borderRadius: 8, border: '1px solid #e2e8f0', objectFit: 'cover', height: 200 }} />
                      </a>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Documents List */}
            {docs.length > 0 && (
              <Box mt={6}>
                <Typography variant="h6" fontWeight={600} mb={2}>
                  Attachments
                </Typography>

                <Box display="flex" flexDirection="column" gap={1.5}>
                  {docs.map((doc, idx) => (
                    <a
                      key={idx}
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: "none" }}
                    >
                      <Card
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          p: 2,
                          bgcolor: "#f8fafc",
                          "&:hover": { bgcolor: "#f1f5f9" },
                          boxShadow: "none",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: "primary.light",
                            color: "primary.main",
                            mr: 2,
                          }}
                        >
                          <ArticleIcon />
                        </Avatar>

                        <Box flex={1}>
                          <Typography variant="body1" fontWeight={500} color="text.primary">
                            {doc.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Document
                          </Typography>
                        </Box>

                        <DownloadIcon color="action" />
                      </Card>
                    </a>
                  ))}
                </Box>
              </Box>
            )}

            {/* Previous & Next Article Footer */}
            {(article.prevArticle || article.nextArticle) && (
              <Box
                sx={{
                  mt: 6,
                  pt: 3,
                  borderTop: '1px solid #e2e8f0',
                  display: 'flex',
                  gap: 3,
                  flexDirection: { xs: 'column', sm: 'row' }
                }}
              >
                {article.prevArticle && (
                  <Box
                    onClick={() => navigate(`/knowledge-base/${article.prevArticle.moduleSlug}/${article.prevArticle.id}`)}
                    sx={{
                      p: 2.5,
                      border: '1px solid #e2e8f0',
                      borderRadius: 2,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: '#f8fafc', borderColor: 'primary.main' },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      flex: 1,
                      minWidth: 0
                    }}
                  >
                    <Typography variant="h5" sx={{ color: 'text.secondary', fontWeight: 300 }}>&larr;</Typography>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Previous</Typography>
                      <Typography variant="body2" fontWeight={600} noWrap sx={{ color: 'text.primary' }}>{article.prevArticle.title}</Typography>
                    </Box>
                  </Box>
                )}
                {article.nextArticle && (
                  <Box
                    onClick={() => navigate(`/knowledge-base/${article.nextArticle.moduleSlug}/${article.nextArticle.id}`)}
                    sx={{
                      p: 2.5,
                      border: '1px solid #e2e8f0',
                      borderRadius: 2,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: '#f8fafc', borderColor: 'primary.main' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      flex: 1,
                      minWidth: 0,
                      textAlign: 'right'
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Next</Typography>
                      <Typography variant="body2" fontWeight={600} noWrap sx={{ color: 'text.primary' }}>{article.nextArticle.title}</Typography>
                    </Box>
                    <Typography variant="h5" sx={{ color: 'text.secondary', fontWeight: 300 }}>&rarr;</Typography>
                  </Box>
                )}
              </Box>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={4} lg={3}>
          <Box position="sticky" top={20}>
            <Card sx={{ p: 3, borderRadius: 3, boxShadow: 1 }}>
              <Typography variant="h6" fontWeight={600} mb={2}>
                More in this Module
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" flexDirection="column" gap={2}>
                {article.moreArticles?.length > 0 ? (
                  article.moreArticles.map(a => (
                    <Box
                      key={a.id}
                      onClick={() => navigate(`/knowledge-base/${a.moduleSlug}/${a.id}`)}
                      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                    >
                      <ArticleIcon fontSize="small" sx={{ color: 'text.secondary', mt: 0.5 }} />
                      <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>{a.title}</Typography>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">No other articles in this module.</Typography>
                )}
              </Box>
            </Card>
          </Box>
        </Grid>
      </Grid>

      <KnowledgeBaseFormDialog
        open={formDialog.open}
        onClose={() => setFormDialog({ open: false, tutorial: null })}
        tutorial={formDialog.tutorial}
        modules={allModules}
        fixedModuleSlug={moduleSlug}
        onSaved={() => {
          loadData();
          setFormDialog({ open: false, tutorial: null });
          showSnack("Knowledge Base updated", "success");
        }}
      />

      <TutorialPlayerModal
        open={playerModal.open}
        onClose={() => setPlayerModal({ open: false, tutorial: null })}
        tutorial={playerModal.tutorial}
      />

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
      />
    </Box>
  );
};

export default KnowledgeBaseViewPage;
